import { useEffect, useMemo, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Search, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Global SRN palette — a Cmd-K / Ctrl-K modal accessible from every admin
 * page. Paste (or type) an SRN or partial SRN and hit Enter to jump to
 * the matching case file no matter which tab is active.
 *
 * When the input is empty, shows the 5 most recent SRNs from across the
 * ledger so the desk can jump into fresh case files with zero typing.
 *
 * Keyboard: ↓/↑ walk the rows, ⏎ opens the highlighted one in-app, and
 * ⌘⏎ / Ctrl-⏎ opens it in a new browser tab (fresh window for parallel
 * triage).
 */
export function AdminSRNPalette({ open, onClose, onNavigate }) {
  const { api } = useAuth();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [recents, setRecents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadingRecents, setLoadingRecents] = useState(false);
  const [cursor, setCursor] = useState(0);

  const runSearch = useCallback(async (term) => {
    const clean = (term || "").trim();
    if (!clean) { setRows([]); return; }
    setBusy(true);
    try {
      // Slack-style search — hits the SRN prefix, sender email, inbound
      // message bodies/subjects and the meta blob (RFQ notes, KYC
      // PAN/GSTIN, inquiry preview). Falls back to the old ``srn=``
      // prefix filter for a pasted VNC-SRN- code so exact-code jumps
      // stay instantaneous.
      const looksLikeSrn = /^VNC-SRN-/i.test(clean);
      const param = looksLikeSrn ? `srn=${encodeURIComponent(clean)}` : `q=${encodeURIComponent(clean)}`;
      const r = await api.get(`/admin/srn-ledger?${param}&limit=8`);
      setRows(r.data.rows || []);
    } catch { setRows([]); }
    finally { setBusy(false); }
  }, [api]);

  const loadRecents = useCallback(async () => {
    setLoadingRecents(true);
    try {
      const r = await api.get(`/admin/srn-ledger?limit=5`);
      setRecents(r.data.rows || []);
    } catch { setRecents([]); }
    finally { setLoadingRecents(false); }
  }, [api]);

  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 220);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  useEffect(() => {
    if (open) { loadRecents(); }
    if (!open) { setQ(""); setRows([]); }
    setCursor(0);
  }, [open, loadRecents]);

  // Reset the highlighted row whenever the result list re-computes so
  // the cursor never points at a stale index.
  useEffect(() => { setCursor(0); }, [rows, recents, q]);

  const openRow = (row, { newTab = false } = {}) => {
    if (!row) return;
    if (newTab) {
      // Open the SRN detail in a new browser tab for parallel triage.
      // The URL is our canonical admin route so a hard-load in a fresh
      // window lands on the same case file.
      try {
        const href = `/admin?srn=${encodeURIComponent(row.srn)}`;
        window.open(href, "_blank", "noopener");
      } catch { onNavigate?.(row); }
      return;
    }
    onNavigate?.(row);
    onClose?.();
  };

  const pickCursor = (opts) => {
    const list = (q || "").trim() ? rows : recents;
    if (list.length === 0) return;
    const row = list[Math.max(0, Math.min(cursor, list.length - 1))];
    openRow(row, opts);
  };

  const select = (row) => openRow(row);

  const showingRecents = !q.trim();
  const list = showingRecents ? recents : rows;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-start justify-center pt-[10vh] px-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="admin-srn-palette"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            data-testid="admin-srn-palette-backdrop"
          />
          <motion.div
            initial={{ y: -12, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            exit={{ y: -12, opacity: 0 }} transition={{ duration: 0.16 }}
            className="relative w-full max-w-[640px] bg-[#0B0B0B] border border-white/15 rounded-sm shadow-[0_20px_60px_-15px_rgba(216,178,62,0.35)] overflow-hidden"
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
              <Search className="h-4 w-4 text-[#D8B23E]" />
              <input
                autoFocus
                value={q}
                onChange={(e) => {
                  const v = e.target.value;
                  const looksLikeSrn = /^v?n?c?-?s?r?n?/i.test(v) && v.length <= 20;
                  setQ(looksLikeSrn ? v.toUpperCase() : v);
                }}
                onKeyDown={(e) => {
                  const list = (q || "").trim() ? rows : recents;
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setCursor((c) => Math.min(c + 1, Math.max(0, list.length - 1)));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setCursor((c) => Math.max(0, c - 1));
                  } else if (e.key === "Enter") {
                    e.preventDefault();
                    pickCursor({ newTab: (e.metaKey || e.ctrlKey) });
                  } else if (e.key === "Escape") {
                    onClose?.();
                  }
                }}
                placeholder="Paste SRN, or type text from a message…"
                className="flex-1 bg-transparent outline-none font-mono text-sm text-[#F5F5F0] placeholder:text-[#5A5A55]"
                data-testid="admin-srn-palette-input"
              />
              <button
                type="button"
                onClick={onClose}
                className="p-1 text-[#8A8A82] hover:text-[#F5F5F0] transition-colors"
                aria-label="Close"
                data-testid="admin-srn-palette-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {showingRecents && (
              <div className="px-4 pt-3 pb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#D8B23E]"
                   data-testid="admin-srn-palette-recents-label">
                <Clock className="h-3 w-3" />
                <span>Most recent case files</span>
              </div>
            )}
            <div className="max-h-[50vh] overflow-y-auto" data-testid="admin-srn-palette-results">
              {busy && (
                <div className="px-4 py-6 font-body text-xs text-[#8A8A82]">Searching the SRN ledger…</div>
              )}
              {!busy && !showingRecents && rows.length === 0 && (
                <div className="px-4 py-6 font-body text-xs text-[#8A8A82]" data-testid="admin-srn-palette-empty">
                  No SRN matches for “{q}”.
                </div>
              )}
              {!busy && showingRecents && loadingRecents && (
                <div className="px-4 py-6 font-body text-xs text-[#8A8A82]">Loading the last 5 SRNs…</div>
              )}
              {!busy && showingRecents && !loadingRecents && recents.length === 0 && (
                <div className="px-4 py-6 font-body text-xs text-[#8A8A82]" data-testid="admin-srn-palette-recents-empty">
                  No SRNs on the ledger yet — filed inquiries, RFQs and KYCs will show up here first.
                </div>
              )}
              {!busy && list.map((r, idx) => {
                // If the current query text appears inside an inbound
                // message body (not the SRN itself), surface a short
                // snippet so admins recognise the case at a glance.
                let snippet = null;
                const term = (q || "").trim();
                if (term && !/^VNC-SRN-/i.test(term) && !r.srn?.toLowerCase().includes(term.toLowerCase()) && Array.isArray(r.messages)) {
                  const hit = r.messages.find((m) => (m?.body || "").toLowerCase().includes(term.toLowerCase()));
                  if (hit) {
                    const body = String(hit.body || "");
                    const at = body.toLowerCase().indexOf(term.toLowerCase());
                    const start = Math.max(0, at - 30);
                    snippet = (start > 0 ? "…" : "") + body.slice(start, at + term.length + 60).trim() + (body.length > at + term.length + 60 ? "…" : "");
                  }
                }
                // Per-kind chip colour so admins can eyeball inquiry vs
                // RFQ vs KYC without reading the label.
                const KIND_COLOURS = {
                  rfq: "bg-[#B87333]/15 text-[#B87333] border-[#B87333]/40",
                  quote: "bg-[#B87333]/15 text-[#B87333] border-[#B87333]/40",
                  order: "bg-[#2FB999]/15 text-[#2FB999] border-[#2FB999]/40",
                  kyc: "bg-[#D8B23E]/15 text-[#D8B23E] border-[#D8B23E]/40",
                  inquiry: "bg-white/10 text-[#C9C9C2] border-white/25",
                  refund: "bg-[#E5484D]/15 text-[#E5484D] border-[#E5484D]/40",
                  indirect_commission: "bg-[#7B61FF]/15 text-[#a892ff] border-[#7B61FF]/40",
                  concierge: "bg-[#2E86DE]/15 text-[#6bb4ff] border-[#2E86DE]/40",
                  motion: "bg-[#c0392b]/15 text-[#e57373] border-[#c0392b]/40",
                };
                const chipCls = KIND_COLOURS[r.kind] || "bg-white/8 text-[#8A8A82] border-white/20";
                return (
                <button
                  key={r.srn}
                  type="button"
                  onClick={() => select(r)}
                  onMouseEnter={() => setCursor(idx)}
                  className={`w-full text-left px-4 py-3 flex items-start gap-4 border-b border-white/5 outline-none transition-colors group ${idx === cursor ? "bg-white/[0.07]" : "hover:bg-white/[0.04]"}`}
                  data-testid={`admin-srn-palette-row-${idx}`}
                  data-active={idx === cursor ? "true" : "false"}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`font-mono text-sm transition-colors ${idx === cursor ? "text-[#D8B23E]" : "text-[#F5F5F0] group-hover:text-[#D8B23E]"}`}>{r.srn}</div>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-[#8A8A82]">
                      <span
                        className={`inline-block uppercase tracking-[0.18em] px-2 py-0.5 text-[10px] border rounded-sm ${chipCls}`}
                        data-testid={`admin-srn-palette-kind-${idx}`}
                      >{r.kind}</span>
                      {r.email && <span className="truncate">· {r.email}</span>}
                    </div>
                    {snippet && (
                      <div className="mt-1 font-body text-[11px] text-[#8A8A82] italic line-clamp-2"
                           data-testid={`admin-srn-palette-snippet-${idx}`}>
                        “{snippet}”
                      </div>
                    )}
                  </div>
                  <div className="font-body text-[11px] text-[#5A5A55] whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                  </div>
                </button>
                );
              })}
            </div>
            <div className="px-4 py-2 border-t border-white/10 flex items-center justify-between text-[11px] text-[#5A5A55]">
              <span>Vinci SRN Palette</span>
              <span>
                <kbd className="px-1.5 py-0.5 border border-white/15 rounded text-[10px] mr-1">↑↓</kbd>
                <span>walk</span>
                <span className="mx-2">·</span>
                <kbd className="px-1.5 py-0.5 border border-white/15 rounded text-[10px] mr-1">⏎</kbd>
                <span>open</span>
                <span className="mx-2">·</span>
                <kbd className="px-1.5 py-0.5 border border-white/15 rounded text-[10px] mr-1">⌘⏎</kbd>
                <span>new tab</span>
                <span className="mx-2">·</span>
                <kbd className="px-1.5 py-0.5 border border-white/15 rounded text-[10px] mr-1">Esc</kbd>
                <span>close</span>
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


/**
 * Hook that wires the global ⌘K / Ctrl-K shortcut and controls the
 * palette's open state.
 */
export function useSRNPalette() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      const isMeta = e.metaKey || e.ctrlKey;
      if (isMeta && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return { open, setOpen };
}
